import Axios, { AxiosRequestConfig, AxiosError } from 'axios';
import { parseAxiosError } from '../../errors/parseApiError';
import type { ApiError } from '../../errors/types';
import { API_BASE_URL } from '../apiBaseUrl';
import { useBackendStatusStore } from '../../store/useBackendStatusStore';

// Gateway-Status, die für einen Neustart/Deployment des Backends sprechen — im
// Gegensatz zu 500, das ein Anwendungsfehler bei laufendem Backend ist.
const GATEWAY_STATUSES = [502, 503, 504];

// Base URL für das Backend
const BASE_URL = API_BASE_URL;

// Axios Instance mit Interceptors
export const axiosInstance = Axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // WICHTIG: Cookie-basierte Auth erfordert credentials
  withCredentials: true,
});

// Request Interceptor: Tenant-ID hinzufügen (kein Token mehr - Cookie-basiert!)
axiosInstance.interceptors.request.use(
  (config) => {
    // Tenant-ID aus localStorage (für Multi-Tenancy)
    const tenantId = localStorage.getItem('tenantId');
    if (tenantId) {
      config.headers['X-Tenant-Id'] = tenantId;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response Interceptor: Handle Auth-Errors + attach parsed ApiError for callers
axiosInstance.interceptors.response.use(
  (response) => {
    useBackendStatusStore.getState().reportSuccess();
    return response;
  },
  (error: AxiosError) => {
    // Parse once and attach so downstream code (stores/forms) can read a
    // normalized ApiError without re-parsing on every catch.
    const apiError = parseAxiosError(error);
    (error as AxiosError & { apiError?: ApiError }).apiError = apiError;

    // Backend nicht erreichbar? Kein Response = Transportfehler oder Timeout,
    // 502/503/504 = Gateway antwortet ohne laufende App. Beides deutet auf ein
    // Deployment/Wartungsfenster hin und startet die Health-Probe.
    const isTransportFailure = !error.response;
    const isGatewayFailure =
      error.response !== undefined && GATEWAY_STATUSES.includes(error.response.status);
    if (!Axios.isCancel(error) && (isTransportFailure || isGatewayFailure)) {
      useBackendStatusStore.getState().reportFailure();
    }

    if (error.response?.status === 401) {
      const isLoginEndpoint = error.config?.url?.includes('/api/v1/auth/login');
      const isMeEndpoint = error.config?.url?.includes('/api/v1/auth/me');
      const errorBody = error.response?.data as { error?: string } | undefined;

      // Recognize both the legacy { error: "..." } shape and the new structured
      // contract (apiError.code starts with "common.unauthorized" or
      // "employees.invalidCredentials").
      const isLegacyAuthError =
        errorBody?.error === 'Invalid authentication token' ||
        errorBody?.error === 'Unauthorized access';
      const isContractAuthError =
        apiError.code === 'common.unauthorized' ||
        apiError.code === 'employees.invalidCredentials';

      if (!isLoginEndpoint && !isMeEndpoint && (isLegacyAuthError || isContractAuthError)) {
        localStorage.removeItem('auth-storage');
        // tenantId behalten! Ist noch gültig.
        console.log('401 Unauthorized - Auth state will be cleared by store');
      }
    }

    // Surface traceId for 5xx so support can pivot from a screenshot to logs.
    if (apiError.status >= 500 && apiError.traceId) {
      // eslint-disable-next-line no-console
      console.error('[ApiError 5xx]', apiError.code, 'traceId:', apiError.traceId);
    }

    return Promise.reject(error);
  }
);

// Custom Instance für Orval
export const customInstance = <T>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig
): Promise<T> => {
  const source = Axios.CancelToken.source();

  const promise = axiosInstance({
    ...config,
    ...options,
    cancelToken: source.token,
  }).then(({ data }) => data);

  // @ts-expect-error - Cancel function wird dynamisch hinzugefügt
  promise.cancel = () => {
    source.cancel('Query was cancelled');
  };

  return promise;
};

export default customInstance;
